import { Button, Card, Dialog, DialogPanel, Select, SelectItem, Textarea, TextInput } from "@tremor/react";
import { Check, ChevronRight, Copy, Globe, Pencil, Plus, RefreshCw, Trash2, Webhook, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  createInboundWebhook,
  deleteInboundWebhook,
  rotateInboundWebhookToken,
  updateInboundWebhook,
} from "../api/inboundWebhooks.js";
import { useInboundWebhooks, useInboundWebhookEvents } from "../queries/inboundWebhooks.js";
import { useChannels } from "../queries/channels.js";
import { useSchedules } from "../queries/oncall.js";
import { useQueryClient } from "@tanstack/react-query";
import { keys } from "../queryKeys.js";
import Badge from "../components/Badge.jsx";

const SEVERITIES = ["critical", "high", "medium", "low"];

const EMPTY_FORM = {
  name: "",
  description: "",
  source_label: "",
  channel_id: "",
  on_call_schedule_id: "",
  default_severity: "high",
  auto_resolve: true,
  is_active: true,
};

function SeverityBadge({ severity }) {
  const colors = { critical: "red", high: "orange", medium: "yellow", low: "gray" };
  return <Badge color={colors[severity] ?? "gray"}>{severity}</Badge>;
}

function HealthBadge({ webhook, t }) {
  if (webhook.is_active === false) {
    return <Badge color="gray">{t("health.disabled")}</Badge>;
  }
  if (webhook.error_count > 0) {
    return <Badge color="red">{t("health.errors", { count: webhook.error_count })}</Badge>;
  }
  return <Badge color="emerald">{t("health.active")}</Badge>;
}

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }
  return (
    <button
      type="button"
      onClick={copy}
      className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-800"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? "Copied!" : "Copy"}
    </button>
  );
}

function WebhookEditor({ webhook, channels, onCallSchedules, onClose, onSaved }) {
  const { t } = useTranslation("inbound_webhooks");
  const isEdit = !!webhook;
  const [form, setForm] = useState(() => ({
    ...EMPTY_FORM,
    ...webhook,
    channel_id: webhook?.channel_id ?? "",
    on_call_schedule_id: webhook?.on_call_schedule_id ?? "",
  }));
  const [saving, setSaving] = useState(false);

  function set(field, value) {
    setForm((p) => ({ ...p, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }

    const payload = {
      name: form.name.trim(),
      description: form.description?.trim() || null,
      source_label: form.source_label?.trim() || null,
      channel_id: form.channel_id || null,
      on_call_schedule_id: form.on_call_schedule_id || null,
      default_severity: form.default_severity,
      auto_resolve: form.auto_resolve,
      is_active: form.is_active,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await updateInboundWebhook(webhook.id, payload);
        toast.success(t("editor.success_save"));
      } else {
        await createInboundWebhook(payload);
        toast.success(t("editor.success_create"));
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.error ?? t("errors.save"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-w-lg flex-col rounded-none bg-white p-0 shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-base font-semibold text-gray-900">
            {isEdit ? t("editor.title_edit") : t("editor.title_new")}
          </h2>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">{t("editor.field_name")}</label>
              <TextInput
                required
                value={form.name}
                onValueChange={(v) => set("name", v)}
                placeholder={t("editor.field_name_placeholder")}
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t("editor.field_description")} <span className="font-normal text-gray-400">{t("editor.optional")}</span>
              </label>
              <Textarea
                rows={2}
                value={form.description ?? ""}
                onValueChange={(v) => set("description", v)}
                placeholder={t("editor.field_description_placeholder")}
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t("editor.field_source_label")} <span className="font-normal text-gray-400">{t("editor.optional")}</span>
              </label>
              <TextInput
                value={form.source_label ?? ""}
                onValueChange={(v) => set("source_label", v)}
                placeholder={t("editor.field_source_label_placeholder")}
              />
              <p className="mt-1 text-xs text-gray-400">{t("editor.field_source_label_hint")}</p>
            </div>

            <div className="border-t border-gray-100 pt-5">
              <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-400">Notifications</p>

              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">{t("editor.field_channel")}</label>
                  <Select value={form.channel_id} onValueChange={(v) => set("channel_id", v)}>
                    <SelectItem value="">{t("editor.field_channel_none")}</SelectItem>
                    {channels.map((cg) => (
                      <SelectItem key={cg.id} value={cg.id}>{cg.name}</SelectItem>
                    ))}
                  </Select>
                </div>

                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">{t("editor.field_on_call")}</label>
                  <Select value={form.on_call_schedule_id} onValueChange={(v) => set("on_call_schedule_id", v)}>
                    <SelectItem value="">{t("editor.field_on_call_none")}</SelectItem>
                    {onCallSchedules.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </Select>
                </div>
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">{t("editor.field_severity")}</label>
              <Select value={form.default_severity} onValueChange={(v) => set("default_severity", v)}>
                {SEVERITIES.map((s) => (
                  <SelectItem key={s} value={s}>{t(`severity.${s}`)}</SelectItem>
                ))}
              </Select>
            </div>

            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.auto_resolve}
                onChange={(e) => set("auto_resolve", e.target.checked)}
                className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
              />
              {t("editor.field_auto_resolve")}
            </label>

            {isEdit && (
              <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => set("is_active", e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
                />
                {t("editor.field_active")}
              </label>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
            <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : isEdit ? t("editor.save") : t("editor.create")}
            </Button>
          </div>
        </form>
      </DialogPanel>
    </Dialog>
  );
}

const EVENT_STATUS_COLOR = { accepted: "emerald", rejected: "amber", error: "red" };

function WebhookEventHistory({ webhookId, t }) {
  const { data, isLoading } = useInboundWebhookEvents(webhookId);
  const events = data ?? [];

  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
        {t("detail.events_title")}
      </p>
      {isLoading ? (
        <p className="text-sm text-gray-400">{t("loading")}</p>
      ) : events.length === 0 ? (
        <p className="text-sm text-gray-400">{t("detail.events_empty")}</p>
      ) : (
        <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 text-sm">
          {events.map((e) => (
            <div key={e.id} className="flex items-start gap-3 px-3 py-2">
              <Badge color={EVENT_STATUS_COLOR[e.status] ?? "gray"}>{e.status}</Badge>
              <div className="min-w-0 flex-1">
                <p className="text-gray-700">
                  {e.event}
                  {e.error_message && (
                    <span className="text-gray-400"> — {e.error_message}</span>
                  )}
                </p>
                <p className="mt-0.5 text-xs text-gray-400">
                  {new Date(e.received_at).toLocaleString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WebhookDetail({ webhook, onClose, onEdit, onRotated, onDeleted }) {
  const { t } = useTranslation("inbound_webhooks");
  const [rotating, setRotating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const exampleTrigger = JSON.stringify({
    event: "trigger",
    title: "High CPU on prod-1",
    description: "CPU usage exceeded 95% for 5 minutes",
    severity: "high",
    dedup_key: "prod-1-high-cpu",
    source: webhook.source_label || "my-system",
  }, null, 2);

  const exampleResolve = JSON.stringify({
    event: "resolve",
    dedup_key: "prod-1-high-cpu",
  }, null, 2);

  const curlExample = `curl -X POST "${webhook.webhook_url}" \\
  -H "Content-Type: application/json" \\
  -d '{"event":"trigger","title":"High CPU on prod-1","severity":"high","dedup_key":"prod-1-cpu"}'`;

  async function handleRotate() {
    if (!window.confirm(t("detail.rotate_confirm"))) return;
    setRotating(true);
    try {
      const updated = await rotateInboundWebhookToken(webhook.id);
      toast.success("Webhook URL rotated");
      onRotated(updated);
    } catch {
      toast.error(t("errors.rotate"));
    } finally {
      setRotating(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete webhook "${webhook.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await deleteInboundWebhook(webhook.id);
      toast.success("Webhook deleted");
      onDeleted(webhook.id);
    } catch {
      toast.error(t("errors.delete"));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-w-2xl flex-col rounded-none bg-white p-0 shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 className="truncate text-base font-semibold text-gray-900">{webhook.name}</h2>
              <HealthBadge webhook={webhook} t={t} />
            </div>
            {webhook.description && (
              <p className="mt-0.5 truncate text-xs text-gray-400">{webhook.description}</p>
            )}
          </div>
          <div className="ml-4 flex items-center gap-2">
            <Button size="xs" variant="secondary" icon={Pencil} onClick={onEdit}>Edit</Button>
            <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">

          {/* Webhook URL */}
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">{t("detail.url_label")}</p>
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
              <code className="flex-1 truncate text-xs text-gray-800">{webhook.webhook_url}</code>
              <CopyButton value={webhook.webhook_url} />
            </div>
            <p className="mt-1 text-xs text-gray-400">{t("detail.url_hint")}</p>
            <button
              type="button"
              onClick={handleRotate}
              disabled={rotating}
              className="mt-2 inline-flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              {rotating ? "Rotating…" : t("detail.rotate")}
            </button>
          </div>

          {/* Config summary */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
              <p className="text-xs font-medium text-gray-500">{t("detail.notify_label")}</p>
              <p className="mt-1 font-medium text-gray-900">
                {webhook.channel_name || webhook.on_call_schedule_name || <span className="text-gray-400">—</span>}
              </p>
            </div>
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
              <p className="text-xs font-medium text-gray-500">Default severity</p>
              <div className="mt-1"><SeverityBadge severity={webhook.default_severity} /></div>
            </div>
            <div className="col-span-2 rounded-lg border border-gray-100 bg-gray-50 p-3">
              <p className="text-xs font-medium text-gray-500">Auto-resolve</p>
              <p className="mt-1 text-sm text-gray-900">
                {webhook.auto_resolve ? t("detail.auto_resolve_on") : t("detail.auto_resolve_off")}
              </p>
            </div>
          </div>

          {/* Accepted parameters */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">{t("detail.docs_title")}</p>
            <p className="mb-3 text-sm text-gray-500">{t("detail.docs_intro")}</p>
            <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 text-sm">
              {[
                ["event", t("params.event")],
                ["title", t("params.title")],
                ["description", t("params.description")],
                ["severity", t("params.severity")],
                ["dedup_key", t("params.dedup_key")],
                ["source", t("params.source")],
              ].map(([param, desc]) => (
                <div key={param} className="flex gap-3 px-3 py-2">
                  <code className="w-28 shrink-0 text-teal-700">{param}</code>
                  <span className="text-gray-600">{desc}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Example — trigger */}
          <div>
            <div className="mb-1 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{t("detail.example_title")}</p>
              <CopyButton value={curlExample} />
            </div>
            <pre className="overflow-x-auto rounded-lg bg-gray-900 px-4 py-3 text-xs text-gray-100">{curlExample}</pre>
          </div>

          {/* Example — resolve */}
          <div>
            <div className="mb-1 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{t("detail.example_resolve_title")}</p>
              <CopyButton value={`curl -X POST "${webhook.webhook_url}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify({ event: "resolve", dedup_key: "prod-1-cpu" })}'`} />
            </div>
            <pre className="overflow-x-auto rounded-lg bg-gray-900 px-4 py-3 text-xs text-gray-100">{`curl -X POST "${webhook.webhook_url}" \\
  -H "Content-Type: application/json" \\
  -d '{"event":"resolve","dedup_key":"prod-1-cpu"}'`}</pre>
          </div>

          {/* Recent events */}
          <WebhookEventHistory webhookId={webhook.id} t={t} />

          {/* Danger zone */}
          <div className="border-t border-gray-100 pt-4">
            <Button
              variant="secondary"
              color="red"
              icon={Trash2}
              onClick={handleDelete}
              disabled={deleting}
              size="xs"
            >
              {deleting ? "Deleting…" : "Delete webhook"}
            </Button>
          </div>
        </div>
      </DialogPanel>
    </Dialog>
  );
}

export default function InboundWebhooks() {
  const { t } = useTranslation("inbound_webhooks");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);

  const qc = useQueryClient();
  const webhooksQuery = useInboundWebhooks();
  const channelsQuery = useChannels();
  const schedulesQuery = useSchedules();
  const webhooks = webhooksQuery.data ?? [];
  const channels = channelsQuery.data ?? [];
  const onCallSchedules = schedulesQuery.data ?? [];
  const loading =
    webhooksQuery.isPending || channelsQuery.isPending || schedulesQuery.isPending;

  useEffect(() => {
    if (webhooksQuery.isError) toast.error(t("errors.load"));
  }, [webhooksQuery.isError, t]);

  const load = () =>
    qc.invalidateQueries({ queryKey: keys.inboundWebhooks.all });

  function openCreate() {
    setEditing(null);
    setEditorOpen(true);
  }

  function openEdit(wh) {
    setEditing(wh);
    setSelected(null);
    setEditorOpen(true);
  }

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("title")}</h1>
          <p className="mt-1 text-sm text-gray-500">{t("subtitle")}</p>
        </div>
        <Button icon={Plus} onClick={openCreate}>{t("new")}</Button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400">{t("loading")}</p>
      ) : webhooks.length === 0 ? (
        <Card className="px-5 py-12 text-center">
          <Webhook className="mx-auto mb-3 h-8 w-8 text-gray-300" />
          <p className="text-sm font-medium text-gray-700">{t("empty.title")}</p>
          <p className="mt-1 text-sm text-gray-400">{t("empty.description")}</p>
          <Button className="mt-4" icon={Plus} onClick={openCreate}>{t("new")}</Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {webhooks.map((wh) => (
            <Card
              key={wh.id}
              className="cursor-pointer p-0 transition hover:shadow-md"
              onClick={() => setSelected(wh)}
            >
              <div className="flex items-center gap-4 px-5 py-4">
                <Globe className="h-8 w-8 shrink-0 rounded-lg bg-teal-50 p-1.5 text-teal-600" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-gray-900">{wh.name}</p>
                    <HealthBadge webhook={wh} t={t} />
                    <SeverityBadge severity={wh.default_severity} />
                    {wh.source_label && (
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">{wh.source_label}</span>
                    )}
                  </div>
                  {wh.description && (
                    <p className="mt-0.5 truncate text-sm text-gray-500">{wh.description}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-400">
                    Notifies: <span className="font-medium">{wh.channel_name || wh.on_call_schedule_name || "—"}</span>
                    {" · "}
                    {wh.auto_resolve ? "auto-resolve" : "manual resolve"}
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-gray-300" />
              </div>
            </Card>
          ))}
        </div>
      )}

      {editorOpen && (
        <WebhookEditor
          webhook={editing}
          channels={channels}
          onCallSchedules={onCallSchedules}
          onClose={() => setEditorOpen(false)}
          onSaved={async () => {
            setEditorOpen(false);
            await load();
          }}
        />
      )}

      {selected && (
        <WebhookDetail
          webhook={selected}
          onClose={() => setSelected(null)}
          onEdit={() => openEdit(selected)}
          onRotated={(updated) => {
            setSelected(updated);
            load();
          }}
          onDeleted={() => {
            setSelected(null);
            load();
          }}
        />
      )}
    </div>
  );
}
